import * as RadPopover from '@radix-ui/react-popover'
import { useAtom } from '@reatom/npm-react'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Set } from '@blade/std-lib'
import { Button, useBorderBoxSize } from '@blade/ui-kit'
import {
  ArrowTriangleDownIcon,
  ArrowTriangleUpIcon,
  Checkbox,
} from '@root/shared/ui-kit'

import { typeFilterAtom } from '../atoms/reatom-tracking-wallets-alerts-list'

import type { AlertTypeFilter } from '../atoms/reatom-tracking-wallets-alerts-list'
import type { ClassNameProps } from '@root/shared/react'

type WalletAlertsFiltersProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
} & ClassNameProps

// TODO: move to ui-kit
export const WalletAlertsFilters: React.FC<WalletAlertsFiltersProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation()
  const refTrigger = useRef<HTMLButtonElement>(null)
  const triggerWidth = useBorderBoxSize(refTrigger).inlineSize
  const [typeFilter, setTypeFilter] = useAtom(typeFilterAtom)

  const toggleValue = (value: AlertTypeFilter) => {
    setTypeFilter(Set.toggle(value)(typeFilter))
  }

  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <Trigger
        ref={refTrigger}
        open={open}
        onClick={() => onOpenChange(!open)}
        asChild
      >
        <FilterButton
          variant="secondary"
          radius="rounded"
          size="xxs"
          iconRight={<ArrowTriangleDownIcon />}
        >
          <span>{t('common.filter_other')}</span>
        </FilterButton>
      </Trigger>
      <RadPopover.Portal>
        <Content
          side="bottom"
          align="center"
          // TODO: Val — check performance
          style={{ width: triggerWidth || undefined }}
        >
          <ButtonExpanded
            variant="ghost"
            radius="rounded"
            size="xxs"
            iconRight={<ArrowTriangleUpIcon />}
            onClick={() => onOpenChange(!open)}
          >
            <span>{t('common.filter_other')}</span>
          </ButtonExpanded>
          <Items>
            <Item>
              <Checkbox
                id="buy"
                onCheckedChange={() => toggleValue('buy')}
                checked={typeFilter.has('buy')}
              />
              <Label htmlFor="buy">{t('trading.buys')}</Label>
            </Item>
            <Item>
              <Checkbox
                id="sell"
                onCheckedChange={() => toggleValue('sell')}
                checked={typeFilter.has('sell')}
              />
              <Label htmlFor="sell">{t('trading.sells')}</Label>
            </Item>
          </Items>
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const Root = styled(RadPopover.Root)`
  position: relative;
`

const Trigger = styled(RadPopover.Trigger)<{
  open: boolean
}>``

const Content = styled(RadPopover.Content)`
  background: var(--background-2);

  backdrop-filter: blur(35px);
  border-radius: 6px;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

  margin-top: -24px;
`

const Items = styled.div`
  padding: 8px;
  gap: 8px;
  display: grid;
`

const Item = styled.div`
  color: var(--text-11);

  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr;

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  gap: 5px;
  transition: all 100ms ease-in;
  width: 70px;

  &:hover {
    color: var(--text-12);
  }
`

const Label = styled.label`
  cursor: pointer;
`

const FilterButton = styled(Button)`
  --button-radius-rounded: 6px;

  display: grid;
  grid-template-columns: 50px 20px;
  padding-inline: 4px;
`

const ButtonExpanded = styled(FilterButton)`
  --button-radius-rounded: 0;
  border: 1px solid transparent;
  background: transparent;
  border-bottom: 1px solid var(--background-4);

  width: 100%;
  cursor: pointer;

  display: grid;
  grid-template-columns: 50px 20px;

  > svg {
    overflow: hidden;
  }

  &:hover {
    background: transparent;
    border-color: transparent;
    border-bottom: 1px solid var(--background-4);
  }
`
